import GenericTable from "components/table";
import { Subscription } from "types";
import React, { useState, useEffect, useMemo } from "react";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import CreateSubscriptionDialog from "./CreateSubscriptionDialog";
import EditSubscriptionDialog from "./EditSubscriptionDialog";
import { useTranslation } from "react-i18next";
// Pagination import removed

type SubscriptionsTableProps = {
  listManagement: {
    data: Subscription[];
    total: number;
    isLoading: boolean;
    error: unknown;
    refetch: () => void;
  };
  initialSubscriptionId?: string | null;
};

const SubscriptionsTable: React.FC<SubscriptionsTableProps> = ({ 
  listManagement,
  initialSubscriptionId
}) => {
  const { t } = useTranslation();
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  // Create dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Helper function to get subscription status
  const getSubscriptionStatus = (subscription: Subscription) => {
    const isExpired = subscription.end_date && dayjs().isAfter(dayjs(subscription.end_date));
    
    if (isExpired) {
      return { 
        text: t("subscriptions.status.expired"), 
        class: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' 
      };
    } else if (subscription.is_active) {
      return { 
        text: t("subscriptions.status.active"), 
        class: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
      };
    } else {
      return { 
        text: t("subscriptions.status.inactive"), 
        class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
      };
    }
  };

  // Memoize data with computed status
  const dataWithComputedStatus = useMemo(() => {
    return listManagement.data.map((subscription) => ({
      ...subscription,
      computedStatus: getSubscriptionStatus(subscription)
    }));
  }, [listManagement.data]);

  const handleCreate = () => {
    setIsCreateDialogOpen(true);
  };

  const handleViewSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsViewDialogOpen(true);
  };

  // Auto-open dialog if initialSubscriptionId is provided (from notification)
  useEffect(() => {
    if (initialSubscriptionId && !isViewDialogOpen && listManagement.data.length > 0) {
      const subscription = listManagement.data.find((sub: Subscription) => sub.id === initialSubscriptionId);
      if (subscription) {
        handleViewSubscription(subscription);
      }
    }
  }, [initialSubscriptionId, listManagement.data, isViewDialogOpen]);

  const columns = [
    {
      accessorKey: "name",
      header: () => <p>{t("common.name")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white max-w-xs truncate">
          {info.getValue() || t("library.noNameProvided")}
        </p>
      ),
    },
    {
      accessorKey: "is_active",
      header: () => <p>{t("library.status")}</p>,
      cell: (info: any) => {
        const subscription = info.row.original as any;
        const status = subscription.computedStatus;
        
        return (
          <div className="flex items-center">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.class}`}>
              {status.text}
            </span>
          </div>
        );
      },
      meta: {
        filterable: true,
        filterOptions: [
          { label: t("subscriptions.status.active"), value: "active" },
          { label: t("subscriptions.status.inactive"), value: "inactive" },
          { label: t("subscriptions.status.expired"), value: "expired" },
        ],
      },
      filterFn: (row: any, columnId: string, filterValue: string[]) => {
        const subscription = row.original as Subscription;
        
        // Determine status from subscription data directly
        const isExpired = subscription.end_date && dayjs().isAfter(dayjs(subscription.end_date));
        let currentStatus = '';
        
        if (isExpired) {
          currentStatus = 'expired';
        } else if (subscription.is_active) {
          currentStatus = 'active';
        } else {
          currentStatus = 'inactive';
        }
        
        return filterValue.includes(currentStatus);
      },
    },
    {
      accessorKey: "start_date",
      header: () => <p>{t("library.startDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue() ? dayjs(info.getValue()).format(DATE_TIME_FORMAT) : t("apiKeys.notAvailable")}
        </p>
      ),
    },
    {
      accessorKey: "end_date",
      header: () => <p>{t("library.endDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue() ? dayjs(info.getValue()).format(DATE_TIME_FORMAT) : t("apiKeys.notAvailable")}
        </p>
      ),
    },
    {
      accessorKey: "created_at",
      header: () => <p>{t("library.createdDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue() ? dayjs(info.getValue()).format(DATE_TIME_FORMAT) : t("apiKeys.notAvailable")}
        </p>
      ),
    },
    {
      accessorKey: "updated_at",
      header: () => <p>{t("library.updatedDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue() ? dayjs(info.getValue()).format(DATE_TIME_FORMAT) : t("apiKeys.notAvailable")}
        </p>
      ),
    },
  ];

  const dataWithActions = dataWithComputedStatus.map((row) => ({
    ...row,
  }));

  return (
    <div className="flex flex-col h-full">
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("subscriptions.searchSubscriptions")}
        debug={true}
        onRowClick={(item) => {
          handleViewSubscription(item);
        }}
        onAdd={handleCreate}
        addLabel={t("subscriptions.addSubscription")}
        maxHeight="flex-1"
      />

      <div className="mt-auto">
        {/* Pagination removed */}
      </div>
      
      <CreateSubscriptionDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={listManagement.refetch}
      />

      <EditSubscriptionDialog
        isOpen={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        onSuccess={listManagement.refetch}
        subscription={selectedSubscription}
      />
    </div>
  );
};

export default SubscriptionsTable;
